import { Fragment, createElement, isValidElement, useEffect, useSyncExternalStore } from 'react';
import { useLocation } from 'react-router-dom';
import { IntlMessageFormat } from 'intl-messageformat';

import { API_COPY, API_COPY_LOCALES } from './EsConstants';
import { esFetch } from './EsFetch';

/*
 * The copy store: one locale's bundle from esback (GET /api/v1/copy/{tag}), held at module
 * level so a constant or a helper can ask for a string without a hook (esCopy) and a component
 * re-renders when the bundle lands (useEsCopy).
 *
 * esback resolves the chain -- es-MX reads es-419, then es, then en-US -- into ONE flat bundle,
 * so the client never walks a fallback of its own. Its last resort is the literal the call
 * site wrote, then the key: a key the bundle lacks reads as English rather than as a blank.
 *
 * NOTHING HERE ASKS ON ITS OWN. EsLocaleProvider calls esCopyLoad only while rolloutEsTranslations is
 * on; off, the store stays empty, esCopy answers every literal, and no request leaves.
 *
 * FRESHNESS without a socket: a route change rechecks at most once a minute with If-None-Match
 * (a 304 is the common answer), and an admin or translator save posts on the 'es-copy'
 * BroadcastChannel so this browser's other tabs recheck at once.
 *
 * ONE TARGET, THE LATEST ASKED FOR. A pick, a recheck, and a save announced from another tab each
 * send a request, and the network answers them in any order. Until 2026-10-19 every 200 was
 * published as it landed and a recheck asked for the tag HELD rather than the tag wanted, so an
 * English answer already on its way when the reader picked French landed after the French one and
 * the page went back to English until a reload (Tim, on local). gWant is the tag and reader the
 * provider last asked for; an answer for anything else is dropped, and a recheck asks for gWant.
 */

export const COPY_CHANNEL = 'es-copy';
export const COPY_RECHECK_MS = 60 * 1000;
// jest.setup.js fails a test whose render warns with this prefix: a key the bundle lacks is a
// seed file that forgot it, and outside production it should be loud.
export const COPY_MISSING = 'es-copy: missing key';

export const COPY_EMPTY = Object.freeze( { tag: null, group: null, chain: [], etag: null, text: {}, media: {}, loaded: false } );

let gState = COPY_EMPTY;
let gWho = null;
let gWant = null;
let gChecked = 0;
let gLoading = null;
let gLocales = null;
let gChannel = null;
const gListeners = new Set();
const gFormats = new Map();
const gWarned = new Set();

const publish = ( pState ) => {
    gState = pState;
    gListeners.forEach( ( pListener ) => pListener() );
};

const subscribe = ( pListener ) => {
    gListeners.add( pListener );
    return () => gListeners.delete( pListener );
};

const snapshot = () => gState;

function listen() {
    if ( gChannel || typeof BroadcastChannel === 'undefined' ) return;
    gChannel = new BroadcastChannel( COPY_CHANNEL );
    gChannel.onmessage = () => {
        if ( gWant ) esCopyLoad( gWant.tag, { who: gWant.who, force: true } );
    };
}

/**
 * Loads pTag's bundle, or rechecks the one held. pWho names the reader (an account id, or
 * null signed out): esback hides admin keys from everybody else, so a new reader asks afresh
 * rather than trusting an ETag earned by the last one. force skips the once-a-minute wait.
 */
export function esCopyLoad( pTag, { who = null, force = false } = {} ) {
    if ( !pTag ) return Promise.resolve( gState );
    gWant = { tag: pTag, who };

    const aSame = pTag === gState.tag && who === gWho && gState.loaded;
    if ( aSame && !force && Date.now() - gChecked < COPY_RECHECK_MS ) return Promise.resolve( gState );
    if ( gLoading?.tag === pTag && gLoading.who === who ) return gLoading.promise;

    listen();
    const aHeaders = aSame && gState.etag ? { 'If-None-Match': gState.etag } : {};
    const aPromise = esFetch( API_COPY + '/' + encodeURIComponent( pTag ), { headers: aHeaders } )
        .then( async ( pResponse ) => {
            // Overtaken: the reader asked for another locale, or became another reader, since.
            if ( gWant?.tag !== pTag || gWant?.who !== who ) return gState;
            gChecked = Date.now();
            // A 304 is not ok, and is the answer most rechecks get: the bundle held is current. It
            // only ever answers an If-None-Match, which only the held tag's own recheck sends.
            if ( pResponse.status === 304 || !pResponse.ok ) return gState;

            const aBody = await pResponse.json();
            if ( gWant?.tag !== pTag || gWant?.who !== who ) return gState;
            gWho = who;
            publish( {
                tag: aBody?.tag ?? pTag,
                group: aBody?.group ?? null,
                chain: Array.isArray( aBody?.chain ) ? aBody.chain : [],
                etag: pResponse.headers?.get?.( 'ETag' ) ?? aBody?.etag ?? null,
                text: aBody?.text ?? {},
                media: aBody?.media ?? {},
                loaded: true,
            } );
            return gState;
        } )
        // esback silent: the literals stand, and the next route change asks again.
        .catch( () => gState )
        .finally( () => { if ( gLoading?.promise === aPromise ) gLoading = null; } );

    gLoading = { tag: pTag, who, promise: aPromise };
    return aPromise;
}

/** The locale groups, once per session: [ { key, name, nativeName, tier, rtl, own, quality, live } ]. */
export function esCopyLocales() {
    if ( !gLocales ) {
        gLocales = esFetch( API_COPY_LOCALES )
            .then( ( pResponse ) => ( pResponse.ok ? pResponse.json() : [] ) )
            .then( ( pList ) => ( Array.isArray( pList ) ? pList : [] ) )
            .catch( () => [] );
    }
    return gLocales;
}

/** Rechecks the wanted bundle if a minute has passed; a no-op before the first load. */
export const esCopyRecheck = () => ( gWant ? esCopyLoad( gWant.tag, { who: gWant.who } ) : Promise.resolve( gState ) );

/** Tells this browser's other tabs that copy changed. For the admin and translator saves. */
export function esCopyAnnounce() {
    if ( typeof BroadcastChannel === 'undefined' ) return;
    const aChannel = new BroadcastChannel( COPY_CHANNEL );
    aChannel.postMessage( 'changed' );
    aChannel.close();
}

/** For tests: forget the bundle, the locales, and every warning, and close the channel. */
export function esCopyReset() {
    gChannel?.close();
    gChannel = null;
    gWho = null;
    gWant = null;
    gChecked = 0;
    gLoading = null;
    gLocales = null;
    gFormats.clear();
    gWarned.clear();
    publish( COPY_EMPTY );
}

/** The tag the held bundle was asked for (es-MX, not es-419), or null before one loads. */
export const esCopyTag = () => gState.tag;

// Today's {name} fill, kept for literals: running a plain "{count} players" through ICU would
// start grouping its digits, which flag-off rendering must never do. A literal written in
// ICU's own syntax ({count, plural, ...}) has no other reading, so that one goes through ICU.
const plainFill = ( pText, pVars ) => !pVars ? pText
    : String( pText ).replace( /\{(\w+)\}/g, ( pAll, pName ) => ( pName in pVars ? String( pVars[ pName ] ) : pAll ) );

const ICU_ARGUMENT = /\{\s*\w+\s*,/;

function icu( pPattern, pTag, pVars ) {
    const aKey = pTag + '\u0000' + pPattern;
    let aFormat = gFormats.get( aKey );
    if ( !aFormat ) {
        aFormat = new IntlMessageFormat( pPattern, pTag );
        gFormats.set( aKey, aFormat );
    }
    return String( aFormat.format( pVars ) );
}

// A malformed pattern and a missing argument both throw; either way the reader gets the
// text with what can be filled, not an exception.
function format( pPattern, pTag, pVars ) {
    try {
        return icu( pPattern, pTag, pVars );
    }
    catch {
        return plainFill( pPattern, pVars );
    }
}

function literal( pLiteral, pKey, pVars ) {
    const aText = pLiteral ?? pKey;
    return ICU_ARGUMENT.test( aText ) ? format( aText, 'en-US', pVars ) : plainFill( aText, pVars );
}

function warnMissing( pKey ) {
    if ( process.env.NODE_ENV === 'production' || gWarned.has( pKey ) ) return;
    gWarned.add( pKey );
    console.warn( COPY_MISSING + ' ' + pKey + ' in ' + gState.tag );
}

// A var that is a React element -- a link, a <b>, a <code> -- fills its placeholder as itself,
// so a sentence with a link in it is ONE message the translator orders, not pieces this code
// joins with spaces ja, ko, and zh do not use. The element travels through ICU as a private-use
// marker and the formatted text is cut back into parts around it.
const SLOT = '\uE000';
const SLOT_SPLIT = /\uE000(\d+)\uE000/;

function slotted( pVars ) {
    let aNodes = null;
    const aVars = {};
    for ( const [ aName, aValue ] of Object.entries( pVars ) ) {
        if ( isValidElement( aValue ) ) {
            aNodes ??= [];
            aVars[ aName ] = SLOT + aNodes.length + SLOT;
            aNodes.push( aValue );
        }
        else aVars[ aName ] = aValue;
    }
    return aNodes && { vars: aVars, nodes: aNodes };
}

function parts( pText, pNodes ) {
    const aParts = String( pText ).split( SLOT_SPLIT )
        .map( ( pPart, pIndex ) => ( pIndex % 2 ? pNodes[ Number( pPart ) ] : pPart ) )
        .filter( ( pPart ) => pPart !== '' );
    return createElement( Fragment, null, ...aParts );
}

/**
 * One string from pState (the held bundle by default): its text, else the literal, else the key.
 * A string, or a fragment when a var is a React element.
 */
export function esCopy( pKey, pLiteral, pVars, pState = gState ) {
    const aSlotted = pVars && slotted( pVars );
    return aSlotted ? parts( copyText( pKey, pLiteral, aSlotted.vars, pState ), aSlotted.nodes )
        : copyText( pKey, pLiteral, pVars, pState );
}

function copyText( pKey, pLiteral, pVars, pState ) {
    const aText = pState.text[ pKey ];
    if ( aText === undefined || aText === null ) {
        // Only once the bundle is in: before it, every key is missing and all of them are fine.
        if ( pState.loaded && pKey ) warnMissing( pKey );
        return literal( pLiteral, pKey, pVars );
    }
    // No argument and nothing ICU would unquote: the text is the answer.
    if ( !pVars && !/[{']/.test( aText ) ) return aText;
    return format( aText, pState.tag ?? 'en-US', pVars );
}

/** The held bundle; re-renders the caller when another one lands. */
export const useEsCopy = () => useSyncExternalStore( subscribe, snapshot, snapshot );

/**
 * Rechecks on a route change, at most once a minute. Mounted once, in the nav's locale menu,
 * which is on every route; a no-op while nothing has loaded, so flag-off asks nothing.
 */
export function useEsCopyRecheck() {
    const { pathname } = useLocation();
    useEffect( () => {
        esCopyRecheck();
    }, [ pathname ] );
}
